import { QUESTION_ID } from "./helpers";
import { STEP_ROUTES } from "./constants";
import type {
  ArrangementConfig,
  ArrangementState,
  ServiceQuestion,
} from "./types";

export type ArrangementStep = (typeof STEP_ROUTES)[number];

export interface ScreenDescriptor {
  screen_name: string;
  step: ArrangementStep;
  question_id?: string;
}

/**
 * Sections of the Package & Selections step, in render order.
 * PackageAndSelectionsStep renders from this list and getScreenSequence
 * derives the canonical analytics order from it, so the two can't drift.
 */
export type PackageSection =
  | "package"
  | "included-urn"
  | "additional-urns"
  | "keepsakes"
  | "fingerprints"
  | "add-ons";

/**
 * Keepsakes precede fingerprints: the urn and the keepsakes that match it are
 * one merchandise decision, so they sit together, and fingerprints then stands
 * on its own as a time-bounded service question with its own gated jewelry
 * cross-sell. The two grids draw from the same keepsakes catalog but partition
 * it by the `fingerprint` tag, so neither ever shows the other's products
 * regardless of which renders first.
 */
export function getPackageSections(hasIncludedUrn: boolean): PackageSection[] {
  return hasIncludedUrn
    ? [
        "package",
        "included-urn",
        "additional-urns",
        "keepsakes",
        "fingerprints",
        "add-ons",
      ]
    : ["package", "additional-urns", "keepsakes", "fingerprints", "add-ons"];
}

/** Canonical analytics name for a Package & Selections section. */
export function packageSectionScreenName(section: PackageSection): string {
  if (section === "package") return "package-selection";
  // Keep the "services-add-ons" id (not "add-ons") so the existing drop-off
  // funnel stays continuous.
  if (section === "add-ons") return "services-add-ons";
  return section;
}

/**
 * At-need sub-views of the confirmation step, in render order. CompletionStep
 * renders from this list; the analytics screens are `confirmation-<subView>`.
 */
export const AT_NEED_CONFIRMATION_SUB_VIEWS = [
  "review",
  "sign",
  "pay",
] as const;

/**
 * Service questions that render as Services screens, in config order.
 * q-fingerprints is dropped: it renders in the fingerprints section of
 * Package & Selections instead.
 */
export function getServicesScreenQuestions(
  config: ArrangementConfig | null,
): ServiceQuestion[] {
  return (config?.service_questions ?? []).filter(
    (q) => q.id !== QUESTION_ID.FINGERPRINTS,
  );
}

/** Canonical analytics name for a service-question screen. */
export function serviceScreenName(questionId: string): string {
  return `services-${questionId.replace(/^q-/, "")}`;
}

/**
 * Ordered list of canonical screens for the family's actual path. The total
 * and ordering shift with conditional screens (included urn, the config-driven
 * set of service questions, and the at-need vs pre-need confirmation branch),
 * so callers derive screen_index/total_screens from this rather than
 * hardcoding. The step components render from the builders above, so the
 * rendered navigation and this sequence share one source of truth.
 *
 */
export function getScreenSequence(
  state: ArrangementState,
  config: ArrangementConfig | null,
): ScreenDescriptor[] {
  const inclusions =
    config?.package_inclusions[state.selectedPackageId || ""] ?? null;
  const hasIncludedUrn = (inclusions?.eligibleUrnIds.length ?? 0) > 0;

  const screens: ScreenDescriptor[] = getPackageSections(hasIncludedUrn).map(
    (section) => ({
      screen_name: packageSectionScreenName(section),
      step: "packages",
    }),
  );

  for (const q of getServicesScreenQuestions(config)) {
    screens.push({
      screen_name: serviceScreenName(q.id),
      step: "services",
      question_id: q.id,
    });
  }

  if (state.isDeceased === false) {
    screens.push({
      screen_name: "confirmation-quote-delivery",
      step: "confirmation",
    });
  } else {
    // at-need; a null branch defaults to at-need, the common path
    for (const subView of AT_NEED_CONFIRMATION_SUB_VIEWS) {
      screens.push({
        screen_name: `confirmation-${subView}`,
        step: "confirmation",
      });
    }
  }

  return screens;
}

export function getScreenPosition(
  screenName: string,
  sequence: ScreenDescriptor[],
): { screen_index: number | null; total_screens: number } {
  const idx = sequence.findIndex((s) => s.screen_name === screenName);
  // null (not 0) when the screen isn't in the sequence: a 0 reads as a real
  // 1-based position and silently corrupts the funnel, whereas null is
  // filterable downstream. Never throw — this runs in the view effect and in
  // handleContinue, and analytics must not break the family's navigation.
  return {
    screen_index: idx === -1 ? null : idx + 1,
    total_screens: sequence.length,
  };
}
